import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import API from '../../utils/api';
import toast from 'react-hot-toast';

const emptyBill = {
  supplierName: '',
  invoiceNo: '',
  invoiceDate: new Date().toISOString().split('T')[0],
  taxAmount: '',
  invoiceAmount: ''
};

const emptyPayment = {
  supplierName: '',
  amountPaid: '',
  date: new Date().toISOString().split('T')[0],
  mode: 'cash'
};

export default function SupplierTracking() {
  const [activeTab, setActiveTab] = useState('bills'); // 'bills' | 'payments' | 'ledger'
  const [selectedSupplierName, setSelectedSupplierName] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  
  const [billForm, setBillForm] = useState(emptyBill);
  const [payForm, setPayForm] = useState(emptyPayment);
  
  const [loading, setLoading] = useState(true);

  const [savingBill, setSavingBill] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);

  const fetchBills = () => {
    API.get('/suppliers/bills')
      .then(r => setBills(r.data))
      .catch(() => toast.error('Failed to load supplier bills'));
  };

  const fetchPayments = () => {
    API.get('/suppliers/payments')
      .then(r => setPayments(r.data))
      .catch(() => toast.error('Failed to load supplier payments'));
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([API.get('/suppliers/bills'), API.get('/suppliers/payments')])
      .then(([b, p]) => {
        setBills(b.data);
        setPayments(p.data);
      })
      .catch(() => toast.error('Failed to load supplier data'))
      .finally(() => setLoading(false));
  }, []);

  const handleAddBill = async (e) => {
    e.preventDefault();
    if (savingBill) return;
    if (!billForm.supplierName || !billForm.invoiceNo || !billForm.invoiceAmount) {
      return toast.error('Supplier Name, Invoice No, and Amount are required');
    }
    setSavingBill(true);
    try {
      await API.post('/suppliers/bills', billForm);
      toast.success('Supplier invoice recorded');
      setBillForm(emptyBill);
      fetchBills();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to add bill');
    } finally {
      setSavingBill(false);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (savingPayment) return;
    if (!payForm.supplierName || !payForm.amountPaid) {
      return toast.error('Supplier Name and Amount Paid are required');
    }
    setSavingPayment(true);
    try {
      await API.post('/suppliers/payments', payForm);
      toast.success('Supplier payment recorded');
      setPayForm(emptyPayment);
      fetchPayments();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to record payment');
    } finally {
      setSavingPayment(false);
    }
  };

  // Calculate outstanding due balance per supplier
  const getSupplierBalances = () => {
    const map = {};
    bills.forEach(b => {
      const name = b.supplierName?.trim();
      if (!name) return;
      if (!map[name]) map[name] = { name, invoiced: 0, paid: 0 };
      map[name].invoiced += b.invoiceAmount;
    });
    payments.forEach(p => {
      const name = p.supplierName?.trim();
      if (!name) return;
      if (!map[name]) map[name] = { name, invoiced: 0, paid: 0 };
      map[name].paid += p.amountPaid;
    });
    return Object.values(map).map(s => ({
      ...s,
      due: parseFloat((s.invoiced - s.paid).toFixed(2))
    }));
  };

  const supplierBalances = getSupplierBalances();

  // Process supplier ledger items with running balance
  const getSupplierLedgerItems = (name) => {
    if (!name) return [];
    const ledger = [];
    
    bills.filter(b => b.supplierName?.trim().toLowerCase() === name.trim().toLowerCase()).forEach(b => {
      ledger.push({
        type: 'BILL',
        date: b.invoiceDate,
        detail: `Inv# ${b.invoiceNo}`,
        amount: b.invoiceAmount,
        isPayment: false
      });
    });

    payments.filter(p => p.supplierName?.trim().toLowerCase() === name.trim().toLowerCase()).forEach(p => {
      ledger.push({
        type: 'PAYMENT',
        date: p.date,
        detail: p.mode.toUpperCase(),
        amount: p.amountPaid,
        isPayment: true
      });
    });

    ledger.sort((a, b) => new Date(a.date) - new Date(b.date));

    let balance = 0;
    return ledger.map(item => {
      if (item.isPayment) {
        balance -= item.amount;
      } else {
        balance += item.amount;
      }
      return {
        ...item,
        runningBalance: balance
      };
    });
  };

  // Quick fill form when clicking a supplier in summary list
  const selectSupplier = (name) => {
    setSelectedSupplierName(name);
    if (activeTab === 'bills') {
      setBillForm({ ...billForm, supplierName: name });
    } else if (activeTab === 'payments') {
      setPayForm({ ...payForm, supplierName: name });
    }
    toast.success(`Selected supplier: ${name}`);
  };

  const filteredSupplierBalances = supplierBalances.filter(s =>
    s.name.toLowerCase().includes(supplierSearch.toLowerCase())
  );

  return (
    <Layout title="Supplier Bill Tracking & Payments">
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <button 
          className={`btn ${activeTab === 'bills' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setActiveTab('bills')}
        >
          📄 Supplier Bills
        </button>
        <button 
          className={`btn ${activeTab === 'payments' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setActiveTab('payments')}
        >
          💸 Supplier Payments
        </button>
        <button 
          className={`btn ${activeTab === 'ledger' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setActiveTab('ledger')}
        >
          🚚 Supplier Ledger
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Loading supplier data...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: activeTab === 'ledger' ? '2.4fr 1.1fr' : '1fr 1.4fr 1.1fr', gap: 20 }}>
          
          {activeTab === 'ledger' ? (
            /* Column 1 & 2 Combined: Supplier Ledger Report */
            <div className="card" style={{ gridColumn: 'span 2' }}>
              <div className="card-header" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="card-title">Supplier Ledger: {selectedSupplierName || '— Select a Supplier —'}</span>
                {selectedSupplierName && (
                  <button className="btn btn-outline btn-sm" onClick={() => setSelectedSupplierName('')}>Clear Selection</button>
                )}
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {!selectedSupplierName ? (
                  <div className="empty-state" style={{ padding: 60 }}>
                    <div className="empty-icon">🚚</div>
                    <div className="empty-title">No supplier selected</div>
                    <div className="empty-text">Click a supplier from the Dues Summary on the right to view their transaction ledger.</div>
                  </div>
                ) : (
                  <>
                    {/* Supplier Summary Cards */}
                    {(() => {
                      const balObj = supplierBalances.find(s => s.name.toLowerCase() === selectedSupplierName.toLowerCase()) || { invoiced: 0, paid: 0, due: 0 };
                      return (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, padding: '16px 20px', background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-100)' }}>
                          {[
                            { l: 'Total Purchases Invoiced', v: `₹${balObj.invoiced.toFixed(2)}`, color: 'var(--navy)' },
                            { l: 'Total Amount Paid', v: `₹${balObj.paid.toFixed(2)}`, color: 'var(--green)' },
                            { l: 'Outstanding Due Balance', v: `₹${balObj.due.toFixed(2)}`, color: balObj.due > 0 ? 'var(--red)' : 'var(--navy)', fontStyle: 'bold' }
                          ].map(card => (
                            <div key={card.l} style={{ background: 'white', padding: '12px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--gray-200)' }}>
                              <div style={{ fontSize: '0.7rem', color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase' }}>{card.l}</div>
                              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: card.color, marginTop: 4, fontWeight: 700 }}>{card.v}</div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Ledger History List */}
                    <div className="table-wrap">
                      {getSupplierLedgerItems(selectedSupplierName).length === 0 ? (
                        <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-400)' }}>No history logged for this supplier</div>
                      ) : (
                        <table>
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>Date</th>
                              <th>Invoice# / Payment Mode</th>
                              <th style={{ textAlign: 'right' }}>Amount</th>
                              <th style={{ textAlign: 'right' }}>Running Due Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getSupplierLedgerItems(selectedSupplierName).map((item, idx) => (
                              <tr key={idx}>
                                <td>
                                  <span className={`badge ${item.type === 'BILL' ? 'badge-navy' : 'badge-green'}`}>
                                    {item.type}
                                  </span>
                                </td>
                                <td>{new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                <td><strong style={{ color: 'var(--navy)' }}>{item.detail}</strong></td>
                                <td style={{ textAlign: 'right', fontWeight: 600, color: item.type === 'PAYMENT' ? 'var(--green)' : 'var(--navy)' }}>
                                  {item.type === 'PAYMENT' ? '-' : ''}₹{item.amount.toFixed(2)}
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 700, color: item.runningBalance > 0 ? 'var(--red)' : 'var(--green)' }}>
                                  ₹{item.runningBalance.toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Column 1: Record form */}
              {activeTab === 'bills' ? (
                <div className="card" style={{ height: 'fit-content' }}>
                  <div className="card-header"><span className="card-title">Record Supplier Invoice</span></div>
                  <div className="card-body">
                    <form onSubmit={handleAddBill}>
                      <div className="form-group">
                        <label className="form-label">Supplier Name *</label>
                        <input 
                          className="form-control"
                          placeholder="e.g. Philips Lighting"
                          value={billForm.supplierName}
                          onChange={e => setBillForm({ ...billForm, supplierName: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Invoice# *</label>
                          <input 
                            className="form-control"
                            placeholder="e.g. PH-2026-987"
                            value={billForm.invoiceNo}
                            onChange={e => setBillForm({ ...billForm, invoiceNo: e.target.value })}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Invoice Date</label>
                          <input 
                            className="form-control"
                            type="date"
                            value={billForm.invoiceDate}
                            onChange={e => setBillForm({ ...billForm, invoiceDate: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Tax Amount (₹)</label>
                          <input 
                            className="form-control"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            value={billForm.taxAmount}
                            onChange={e => setBillForm({ ...billForm, taxAmount: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Invoice Amount (₹) *</label>
                          <input 
                            className="form-control"
                            type="number"
                            min="0.01"
                            step="0.01"
                            placeholder="0.00"
                            value={billForm.invoiceAmount}
                            onChange={e => setBillForm({ ...billForm, invoiceAmount: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <button type="submit" className="btn btn-gold btn-lg" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} disabled={savingBill}>
                        {savingBill ? '⏳ Saving...' : '➕ Save Invoice Log'}
                      </button>
                    </form>
                  </div>
                </div>
              ) : (
                <div className="card" style={{ height: 'fit-content' }}>
                  <div className="card-header"><span className="card-title">Record Payment To Supplier</span></div>
                  <div className="card-body">
                    <form onSubmit={handleAddPayment}>
                      <div className="form-group">
                        <label className="form-label">Supplier Name *</label>
                        <input 
                          className="form-control"
                          placeholder="e.g. Philips Lighting"
                          value={payForm.supplierName}
                          onChange={e => setPayForm({ ...payForm, supplierName: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Amount Paid (₹) *</label>
                          <input 
                            className="form-control"
                            type="number"
                            min="0.01"
                            step="0.01"
                            placeholder="0.00"
                            value={payForm.amountPaid}
                            onChange={e => setPayForm({ ...payForm, amountPaid: e.target.value })}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Payment Date</label>
                          <input 
                            className="form-control"
                            type="date"
                            value={payForm.date}
                            onChange={e => setPayForm({ ...payForm, date: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Payment Mode</label>
                        <select 
                          className="form-control"
                          value={payForm.mode}
                          onChange={e => setPayForm({ ...payForm, mode: e.target.value })}
                        >
                          <option value="cash">Cash</option>
                          <option value="card">Card</option>
                          <option value="upi">UPI (GPay/PhonePe)</option>
                          <option value="net_banking">Net Banking</option>
                        </select>
                      </div>
                      <button type="submit" className="btn btn-gold btn-lg" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} disabled={savingPayment}>
                        {savingPayment ? '⏳ Recording...' : '💸 Record Payment Log'}
                      </button>
                    </form>
                  </div>
                </div>
              )}

              {/* Column 2: Registry lists */}
              {activeTab === 'bills' ? (
                <div className="card">
                  <div className="card-header"><span className="card-title">Supplier Bill Registry ({bills.length})</span></div>
                  <div className="table-wrap">
                    {bills.length === 0 ? (
                      <div className="empty-state">
                        <div className="empty-icon">📄</div>
                        <div className="empty-title">No supplier invoices logged</div>
                      </div>
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Supplier</th>
                            <th>Invoice#</th>
                            <th>Invoice Date</th>
                            <th style={{ textAlign: 'right' }}>Tax</th>
                            <th style={{ textAlign: 'right' }}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bills.map((b) => (
                            <tr key={b.id}>
                              <td><strong style={{ color: 'var(--navy)' }}>{b.supplierName}</strong></td>
                              <td><span className="badge badge-navy" style={{ textTransform: 'none' }}>{b.invoiceNo}</span></td>
                              <td>{new Date(b.invoiceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                              <td style={{ textAlign: 'right', color: 'var(--gray-500)', fontSize: '0.8rem' }}>₹{b.taxAmount.toFixed(2)}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--navy)' }}>₹{b.invoiceAmount.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              ) : (
                <div className="card">
                  <div className="card-header"><span className="card-title">Supplier Payments History ({payments.length})</span></div>
                  <div className="table-wrap">
                    {payments.length === 0 ? (
                      <div className="empty-state">
                        <div className="empty-icon">💸</div>
                        <div className="empty-title">No supplier payments recorded</div>
                      </div>
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Supplier</th>
                            <th>Payment Date</th>
                            <th>Mode</th>
                            <th style={{ textAlign: 'right' }}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((p) => (
                            <tr key={p.id}>
                              <td><strong style={{ color: 'var(--navy)' }}>{p.supplierName}</strong></td>
                              <td>{new Date(p.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                              <td>
                                <span className={`badge ${p.mode === 'cash' ? 'badge-green' : p.mode === 'card' ? 'badge-navy' : 'badge-amber'}`}>
                                  {p.mode}
                                </span>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--green)' }}>₹{p.amountPaid.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Column 3: Supplier Outstanding Dues Summary */}
          <div className="card">
            <div className="card-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
              <span className="card-title">Dues Summary</span>
              <input 
                className="form-control form-control-sm"
                placeholder="Search supplier name..."
                value={supplierSearch}
                onChange={e => setSupplierSearch(e.target.value)}
                style={{ padding: '6px 10px', fontSize: '0.8rem' }}
              />
            </div>
            <div className="table-wrap">
              {filteredSupplierBalances.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--gray-400)', fontSize: '0.8rem' }}>No supplier logs found</div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Supplier</th>
                      <th style={{ textAlign: 'right' }}>Due Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSupplierBalances.map((s, idx) => (
                      <tr 
                        key={idx} 
                        onClick={() => selectSupplier(s.name)} 
                        style={{ cursor: 'pointer', background: selectedSupplierName === s.name ? 'var(--gold-pale)' : '' }}
                        title="Click to select this supplier"
                      >
                        <td>
                          <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{s.name}</div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--gray-500)', marginTop: 2 }}>
                            Invoiced: ₹{s.invoiced.toFixed(0)} | Paid: ₹{s.paid.toFixed(0)}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                          <span className={`badge ${s.due > 0 ? 'badge-red' : 'badge-green'}`} style={{ fontWeight: 700 }}>
                            ₹{s.due.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

        </div>
      )}
    </Layout>
  );
}
